import React, { useEffect, useState, useCallback, useRef } from 'react';
import { X } from 'lucide-react';
import localVersion from '../version.json';
import { hasActiveCreateActivityDraft } from '../utils/createActivityDraft';
import './AutoUpdater.css';

const VERSION_POLL_MS = 15 * 60 * 1000; // re-check for updates while app stays open
const SAFE_MOMENT_WINDOW_MS = 30 * 1000; // updates found right after open/resume apply immediately
const BANNER_AUTO_HIDE_MS = 10 * 1000;
const RELOAD_GUARD_KEY = 'choruss_auto_reload_version'; // prevents reload loops (one plain reload per version)

function readReloadGuard() {
  try {
    return window.sessionStorage.getItem(RELOAD_GUARD_KEY);
  } catch (_) {
    return null;
  }
}

function writeReloadGuard(version) {
  try {
    window.sessionStorage.setItem(RELOAD_GUARD_KEY, version);
  } catch (_) {
    /* storage unavailable */
  }
}

function hasServiceWorkerSupport() {
  return typeof navigator !== 'undefined' && 'serviceWorker' in navigator;
}

/**
 * Fully automatic updates:
 * - Checks /version.json (network-only) on open, on every resume, and every 15 minutes.
 * - Version mismatch silently kicks off a service-worker update download — never shows UI.
 * - Banner only appears when a new service worker is fully installed and waiting (ready to apply).
 * - Applies immediately at safe moments (just opened / just resumed / app backgrounded).
 * - Without SW support: one guarded plain reload per version at safe moments (no banner).
 */
export default function AutoUpdater({ children }) {
  const [bannerVisible, setBannerVisible] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const waitingRegistration = useRef(null);
  const updateAvailable = useRef(false);
  const remoteVersion = useRef(null);
  const lastSafeMomentAt = useRef(Date.now());
  const reloadScheduled = useRef(false);
  const hideTimerRef = useRef(null);

  const clearHideTimer = useCallback(() => {
    if (hideTimerRef.current) {
      clearTimeout(hideTimerRef.current);
      hideTimerRef.current = null;
    }
  }, []);

  const showBanner = useCallback(() => {
    // Only show when a waiting SW makes Refresh a guaranteed one-tap success.
    if (!waitingRegistration.current?.waiting) return;
    if (reloadScheduled.current) return;
    if (hasActiveCreateActivityDraft()) return;
    if (dismissed) return;

    setBannerVisible(true);
    clearHideTimer();
    hideTimerRef.current = setTimeout(() => {
      setBannerVisible(false);
    }, BANNER_AUTO_HIDE_MS);
  }, [dismissed, clearHideTimer]);

  /**
   * Plain reload with a per-version loop guard.
   * Used only when service workers are unavailable (no waiting-worker path).
   * Returns true if the reload was issued.
   */
  const guardedReload = useCallback(() => {
    const target = remoteVersion.current || 'unknown';
    if (readReloadGuard() === target) return false; // already tried once for this version
    writeReloadGuard(target);
    reloadScheduled.current = true;
    clearHideTimer();
    window.location.reload();
    return true;
  }, [clearHideTimer]);

  const triggerSwUpdateCheck = useCallback(() => {
    if (process.env.NODE_ENV === 'production' && hasServiceWorkerSupport()) {
      navigator.serviceWorker.ready.then((reg) => reg.update()).catch(() => {});
    }
  }, []);

  /**
   * Apply a ready update (waiting service worker).
   * auto=true: only at safe moments / when backgrounded; respects draft check.
   * auto=false: user tapped Refresh — always activates waiting SW then reloads.
   */
  const applyUpdate = useCallback(
    ({ auto = true } = {}) => {
      if (reloadScheduled.current) return;
      if (auto && hasActiveCreateActivityDraft()) return;

      const registration = waitingRegistration.current;
      if (!registration?.waiting) return;

      reloadScheduled.current = true;
      clearHideTimer();
      setBannerVisible(false);

      let reloaded = false;
      const doReload = () => {
        if (reloaded) return;
        reloaded = true;
        window.location.reload();
      };

      const onControllerChange = () => {
        navigator.serviceWorker.removeEventListener('controllerchange', onControllerChange);
        doReload();
      };
      navigator.serviceWorker.addEventListener('controllerchange', onControllerChange);

      try {
        registration.waiting.postMessage({ type: 'SKIP_WAITING' });
      } catch (_) {
        /* ignore */
      }

      // Fallback if the new worker never takes control
      window.setTimeout(() => {
        if (reloaded) return;
        doReload();
      }, 4000);
    },
    [clearHideTimer]
  );

  /**
   * Called only when a new SW is fully installed and waiting.
   * Safe moments / background → silent apply. Mid-use → show banner.
   */
  const markUpdateReady = useCallback(
    (registration) => {
      if (!registration?.waiting) return;

      waitingRegistration.current = registration;
      updateAvailable.current = true;

      const nearSafeMoment =
        Date.now() - lastSafeMomentAt.current <= SAFE_MOMENT_WINDOW_MS;
      const hidden = document.visibilityState === 'hidden';

      if (hidden || nearSafeMoment) {
        applyUpdate({ auto: true });
        return;
      }

      showBanner();
    },
    [applyUpdate, showBanner]
  );

  useEffect(() => {
    const onSwUpdate = (e) => {
      const registration = e.detail?.registration;
      if (!registration?.waiting) return;
      markUpdateReady(registration);
    };

    window.addEventListener('swUpdateAvailable', onSwUpdate);
    return () => window.removeEventListener('swUpdateAvailable', onSwUpdate);
  }, [markUpdateReady]);

  /**
   * Version probe: never shows UI. On mismatch, silently start SW download.
   * Without SW support, one guarded reload at a safe moment (no banner).
   */
  const checkRemoteVersion = useCallback(async () => {
    if (reloadScheduled.current) return;
    try {
      const res = await fetch(`/version.json?t=${Date.now()}`, { cache: 'no-store' });
      if (!res.ok) return;
      const data = await res.json();
      if (!data?.version || data.version === localVersion.version) return;

      remoteVersion.current = data.version;

      if (hasServiceWorkerSupport()) {
        // Kick off background download; banner appears only after swUpdateAvailable.
        triggerSwUpdateCheck();
        return;
      }

      // No SW: apply once per version at a safe moment only (never show banner).
      const nearSafeMoment =
        Date.now() - lastSafeMomentAt.current <= SAFE_MOMENT_WINDOW_MS;
      if (nearSafeMoment || document.visibilityState === 'hidden') {
        guardedReload();
      }
    } catch (_) {
      /* offline or blocked */
    }
  }, [triggerSwUpdateCheck, guardedReload]);

  useEffect(() => {
    // App just opened: safe moment.
    lastSafeMomentAt.current = Date.now();
    checkRemoteVersion();
    triggerSwUpdateCheck();

    // Belt-and-braces: notice a worker that was already waiting before mount
    // (e.g. registration completed before AutoUpdater listened for the event).
    if (process.env.NODE_ENV === 'production' && hasServiceWorkerSupport()) {
      navigator.serviceWorker
        .getRegistration()
        .then((reg) => {
          if (reg?.waiting) markUpdateReady(reg);
        })
        .catch(() => {});
    }

    const pollTimer = window.setInterval(() => {
      checkRemoteVersion();
      triggerSwUpdateCheck();
    }, VERSION_POLL_MS);

    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        if (updateAvailable.current) {
          applyUpdate({ auto: true });
        }
        return;
      }

      // App resumed: safe moment — apply pending ready update or check for one.
      lastSafeMomentAt.current = Date.now();
      if (updateAvailable.current) {
        applyUpdate({ auto: true });
        return;
      }
      checkRemoteVersion();
      triggerSwUpdateCheck();
    };

    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.clearInterval(pollTimer);
      clearHideTimer();
    };
  }, [checkRemoteVersion, applyUpdate, clearHideTimer, triggerSwUpdateCheck, markUpdateReady]);

  const handleRefresh = () => {
    applyUpdate({ auto: false });
  };

  const handleDismiss = () => {
    clearHideTimer();
    setBannerVisible(false);
    setDismissed(true);
  };

  return (
    <>
      {bannerVisible && (
        <div className="update-banner" role="status" aria-live="polite">
          <span className="update-banner-text">New improvements available</span>
          <button type="button" className="update-banner-refresh" onClick={handleRefresh}>
            Refresh
          </button>
          <button
            type="button"
            className="update-banner-dismiss"
            onClick={handleDismiss}
            aria-label="Dismiss update notice"
          >
            <X size={16} strokeWidth={2} aria-hidden="true" />
          </button>
        </div>
      )}
      {children}
    </>
  );
}
